import React, { useCallback, useMemo, useState } from "react";

import { useSimCursor } from "../../sim-cursor/SimCursor";

import { framable, type FramableProps } from "./framable";
import {
    type ProgramConfigControl,
    type ProgramConfigDemo,
    ProgramConfiguration
} from "./ProgramConfiguration";
import type { ProgramConfig } from "./types";
import {
    editNumberFieldBeats,
    type SimScriptStep,
    useSimScript
} from "./useSimScript";

/**
 * Auto-playing, CURSOR-DRIVEN driver for the bare Configuration doc block.
 *
 * Renders {@link ProgramConfiguration} in its draft (creation) mode from a
 * fixture draft held in local state, mounts a simulated pointer
 * ({@link useSimCursor}), and auto-plays a natural playthrough of the form —
 * a believable "someone filling it out." For each control, in order, the
 * cursor:
 *
 *   1. travels to it (via its `data-sim-cursor-key`),
 *   2. paints its forced HOVER visual (holds a beat),
 *   3. "clicks" it (cursor press pulse + the control's forced PRESS/focus
 *      visual),
 *   4. the value/selection lands (so the selected/filled state shows),
 *   5. the forced visual clears and the cursor moves on.
 *
 * …then it hides the cursor, holds the filled frame, and loops.
 *
 * No wallet, RPC, or `orgApiFetch`; the block is self-seeded and independent.
 *
 * This is the FIRST of the five "+New Program" doc blocks and establishes the
 * cursor-playthrough pattern the other four (Policies, Funding, Adding,
 * Viewing) copy:
 *
 *   1. hold the block's draft fixture in local state here,
 *   2. render the props-only View from that state, passing a `demo` prop that
 *      routes forced hover/press to ONE control at a time + a
 *      `data-sim-cursor-key` on every control,
 *   3. describe the scripted beats as an ordered {@link SimScriptStep} list —
 *      for each control: move → hover → press+set → clear,
 *   4. hand `reset` + `steps` to {@link useSimScript}, which fires them on
 *      timers and loops; reduced-motion collapses to the filled frame with no
 *      cursor.
 *
 * Drop-in for external (docs/wiki) consumers:
 *
 *   import "@solomon-apps/shared/sim/index.css";
 *   import { ProgramConfigurationSim } from "@solomon-apps/shared/sim";
 *   <ProgramConfigurationSim />
 */

/** Starting draft — the sequence EDITS the demoed fields from here each cycle.
 *  Opens on the fresh-draft DEFAULTS (Continuous · Variable · User % 100 ·
 *  Distribution 14 · Period 90) so the block is faithful to what a real fresh
 *  draft shows. The two demoed fields OPEN at their real default and are then
 *  edited OFF it: `userPct` opens at 100 (edits down to the non-default 80
 *  split) and `settlementCycleLength` opens at 14 (edits to the non-default
 *  7-day cadence). `null` on either is the CLEARED mid-edit state (renders
 *  blank) between the default and the new typed value. */
const EMPTY_DRAFT: ProgramConfigDraft = {
    // Opens as the fresh-draft default now does: Continuous · Variable (earned)
    // — the Budget Type toggle, User % field, and derived Funding line are
    // already visible without a Program Type click.
    programType: "continuous",
    budgetMode: "open",
    apyMode: "earned",
    baseApy: 0,
    // Demoed — OPENS at the real DEFAULT (100), then the cursor edits it DOWN
    // to the non-default 80 split: focus → clear (blank) → type 8 → 80.
    userPct: 100,
    // Default (hidden while Continuous). Kept at the real default so switching
    // the Program Type would reveal 90, not 0.
    periodDays: 90,
    // Demoed — OPENS at the real DEFAULT (14), then the cursor edits it to the
    // non-default 7-day cadence: focus → clear (blank) → type 7.
    settlementCycleLength: 14,
    settlementCycleUnit: "days",
    // Default budget (only surfaces on the Fixed-Budget path, not demoed here).
    budget: 100000
};

/** The full lifted draft the app keeps in ProgramDetail. `userPct` and
 *  `settlementCycleLength` are `number | null` because the sim clears them to
 *  blank mid-edit; the real app always passes numbers. */
interface ProgramConfigDraft {
    programType: "fixed" | "continuous";
    budgetMode: "fixed" | "open";
    apyMode: "earned" | "base";
    baseApy: number;
    userPct: number | null;
    periodDays: number;
    settlementCycleLength: number | null;
    settlementCycleUnit: "days" | "weeks";
    budget: number;
}

/** The `program` fixture — always draft (creation) mode for this block. */
const PROGRAM_FIXTURE: ProgramConfig = {
    status: "in_progress",
    period: "3m"
};

/** No control focused — the resting demo state (cursor between controls). */
const DEMO_IDLE: ProgramConfigDemo = { control: null, phase: "hover" };

// ── Per-control beat timing (ms) ────────────────────────────────────────
/** Lead before a control's hover beat — the cursor starts travelling here so
 *  it arrives as the hover paints. Travel itself is distance-derived and
 *  capped at 1s inside the cursor primitive. */
const TRAVEL_MS = 520;
/** How long a control holds its forced HOVER visual before the "click". */
const HOVER_HOLD_MS = 420;
/** How long a control holds its forced PRESS/focus visual (the click). The
 *  value lands on this beat too, so the selected/filled state shows under the
 *  press. */
const PRESS_HOLD_MS = 340;

export interface ProgramConfigurationSimProps extends FramableProps {
    /**
     * Pause (ms) after the sequence settles on its fully-populated state
     * before it replays. Defaults to 4500. Set to `-1` to play once and
     * stop.
     */
    loopPauseMs?: number;
}

function ProgramConfigurationSimView({
    loopPauseMs
}: ProgramConfigurationSimProps = {}): React.ReactElement {
    const [draft, setDraft] = useState<ProgramConfigDraft>(EMPTY_DRAFT);
    const [demo, setDemo] = useState<ProgramConfigDemo>(DEMO_IDLE);

    const cursor = useSimCursor();
    const { moveTo: moveCursor, click: clickCursor, hide: hideCursor } = cursor;

    const patch = useCallback((next: Partial<ProgramConfigDraft>): void => {
        setDraft((d) => ({ ...d, ...next }));
    }, []);

    const reset = useCallback((): void => {
        setDraft(EMPTY_DRAFT);
        setDemo(DEMO_IDLE);
        hideCursor();
    }, [hideCursor]);

    // A numeric FIELD control the cursor EDITS a PRE-FILLED value on: the field
    // OPENS reading its real default, then travel → hover → click-to-focus →
    // CLEAR (blank) → the new value TYPES in digit-by-digit. Used for User % and
    // Distribution Schedule — the two value inputs in this block. Thin wrapper
    // over the shared `editNumberFieldBeats` so all three program-builder sims
    // share the one "seed the default, then demo the edit" helper.
    const editControlBeats = useCallback(
        (
            control: ProgramConfigControl,
            cursorKey: string,
            key: keyof ProgramConfigDraft,
            target: number
        ): SimScriptStep[] =>
            editNumberFieldBeats({
                control,
                cursorKey,
                target,
                travelMs: TRAVEL_MS,
                hoverHoldMs: HOVER_HOLD_MS,
                typeStartAfter: 220,
                moveCursor,
                clickCursor,
                setDemo: (c, phase) => setDemo({ control: c, phase }),
                clearValue: () =>
                    patch({ [key]: null } as Partial<ProgramConfigDraft>),
                setValue: (value) =>
                    patch({ [key]: value } as Partial<ProgramConfigDraft>)
            }),
        [moveCursor, clickCursor, patch]
    );

    // Scripted playthrough order: the VARIABLE-budget demo — the path that
    // exercises the User % field and the derived Funding row. The draft now
    // OPENS on Continuous · Variable (earned) with the defaults (User % 100 ·
    // Distribution 14), so Program Type + Budget Type are already on their
    // target values — no click beats for them. To keep the walkthrough a real
    // "configuring" demo, the cursor EDITS two fields OFF their real defaults:
    // User % 100 → 80 (a non-default revenue split) and Distribution Schedule
    // 14 → 7 (weekly, a non-default cadence). Each field OPENS at its default,
    // then focus → clear → type the demo value (so the viewer sees the default
    // before the edit, not a type-from-empty that misrepresents it).
    const steps = useMemo<readonly SimScriptStep[]>(
        () => [
            // User % — OPENS at the default 100, edited DOWN to the non-default
            // 80 split (focus → clear → 8 → 80). The Budget Type already reads
            // Variable (the fresh-draft default), so this field is visible from
            // the start. The Funding row derives to "Any rewards generated are
            // used for distributions."
            ...editControlBeats("user-pct", "cfg-user-pct", "userPct", 80),
            // Distribution Schedule — OPENS at the default 14, edited to the
            // non-default 7-day weekly cadence (focus → clear → 7). The unit
            // stays on its "days" default.
            ...editControlBeats(
                "settlement",
                "cfg-settlement",
                "settlementCycleLength",
                7
            ),
            // Settle: drop the last control's forced visual and fade the
            // cursor out before the loop pause.
            {
                after: PRESS_HOLD_MS + 200,
                label: "settle",
                run: (): void => {
                    setDemo(DEMO_IDLE);
                    hideCursor();
                }
            }
        ],
        [editControlBeats, hideCursor]
    );

    useSimScript({ reset, steps, loopPauseMs });

    return (
        <div ref={cursor.containerRef} className="relative">
            <ProgramConfiguration
                program={PROGRAM_FIXTURE}
                demo={demo}
                programType={draft.programType}
                setProgramType={(programType) => patch({ programType })}
                budgetMode={draft.budgetMode}
                setBudgetMode={(budgetMode) => patch({ budgetMode })}
                apyMode={draft.apyMode}
                setApyMode={(apyMode) => patch({ apyMode })}
                baseApy={draft.baseApy}
                setBaseApy={(baseApy) => patch({ baseApy })}
                userPct={draft.userPct}
                setUserPct={(userPct) => patch({ userPct })}
                periodDays={draft.periodDays}
                setPeriodDays={(periodDays) => patch({ periodDays })}
                settlementCycleLength={draft.settlementCycleLength}
                setSettlementCycleLength={(settlementCycleLength) =>
                    patch({ settlementCycleLength })
                }
                settlementCycleUnit={draft.settlementCycleUnit}
                setSettlementCycleUnit={(settlementCycleUnit) =>
                    patch({ settlementCycleUnit })
                }
                budget={draft.budget}
                setBudget={(budget) => patch({ budget })}
            />
            {cursor.layer}
        </div>
    );
}

/**
 * Auto-playing Configuration doc block. Add `framed` to wrap it in its own
 * fixed-height {@link SimFrame} (the block owns its max height — the consumer
 * passes no pixel value); omit it to render bare.
 */
export const ProgramConfigurationSim = framable(
    ProgramConfigurationSimView,
    "ProgramConfigurationSim"
);
